import React, { useEffect, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Card, CardContent, CardFooter, CardTitle, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { ArrowRightIcon, RefreshCwIcon, SaveIcon } from "lucide-react";
import { AxiosError } from "axios";
import { RoleBasedElement } from "@/features/auth/components/RoleBasedElement";
import { ROLES } from "@/features/auth/constants/roles";
import { useObservatoryNotesUpdate } from "@/features/alojamientos/hooks/useObservatoryNotesUpdate";

import AccomodationGeneralDataForm from "./AccomodationGeneralDataForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { accommodationSchema } from "../../schemas/accomodation.schema";
import { AccomodationFormData } from "@/features/alojamientos/types/accomodation.types";
import ServicesForm from "@/features/alojamientos/components/forms/services/ServicesForm";
import { RatesForm } from "@/features/alojamientos/components/forms/RatesForm";
import RepatForm from "@/features/alojamientos/components/forms/RepatForm";

import { useAccommodationCreate } from "@/features/alojamientos/hooks/useAccommodationCreate";
import { useAccommodationUpdate } from "@/features/alojamientos/hooks/useAccommodationUpdate";
import { useSamplingBlock } from "@/features/alojamientos/hooks/useSamplingBlock";
import { useNotificationCreate } from "@/features/notifications/hooks/useNotificationCreate";
import { getUserRole } from "@/features/auth/utils/auth";
import SamplingBlockCard from "./SamplingBlockCard";

interface AccomodationFormProps {
  mode: "create" | "edit" | "view";
  defaultValues?: Partial<AccomodationFormData>;
  establishmentId?: string;
  onCreated?: (id: string) => void;
}

interface ErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
}

const AccomodationForm = ({
  mode,
  defaultValues = {},
  establishmentId,
  onCreated,
}: AccomodationFormProps) => {
  const [activeTab, setActiveTab] = React.useState("general");

  const { accommodation, isLoading: isLoadingAccommodation, updateAccommodation } = useAccommodationUpdate(
    mode === "edit" ? establishmentId : undefined
  );

  const { mutate: createAccommodation } = useAccommodationCreate();
  const { unlockSampling, isUnlocking } = useSamplingBlock();
  const { createNotification } = useNotificationCreate();
  const { saveNotes, isSaving: isSavingNotes } = useObservatoryNotesUpdate(establishmentId);
  const isBlockedForSampling = accommodation?.samplingBlockReasonId !== null;

  const [observatoryNotesValue, setObservatoryNotesValue] = React.useState(accommodation?.observatoryNotes ?? '');

  useEffect(() => {
    setObservatoryNotesValue(accommodation?.observatoryNotes ?? '');
  }, [accommodation?.observatoryNotes]);

  const isObservatoryEditor = getUserRole() === ROLES.OBSERVATORIO_EDITOR;

  const methods = useForm<AccomodationFormData>({
    resolver: zodResolver(accommodationSchema),
    defaultValues: useMemo(() => ({
      services: {
        cards: [],
        parking: [],
        pool: [],
        recreationalSports: [],
        specialMenus: [],
        spa: false,
        gym: false,
        wifi: false,
        breakfast: false,
        casino: false,
        petsAllowed: false,
        cafeteriaBar: false,
        restaurant: false,
        eventRooms: [],
        isAccessible: false,
      },
      rates: [],
      ...defaultValues,
    }), [defaultValues]),
  });

  useEffect(() => {
    if (mode === "edit" && accommodation) {
      methods.reset({
        ...accommodation,
        services: {
          cards: [],
          parking: [],
          pool: [],
          recreationalSports: [],
          specialMenus: [],
          spa: false,
          gym: false,
          wifi: false,
          breakfast: false,
          casino: false,
          petsAllowed: false,
          cafeteriaBar: false,
          restaurant: false,
          eventRooms: [],
          isAccessible: false,
          ...accommodation.services,
        },
        rates: accommodation.rates || [],
      });
    } else if (mode === "create") {
      methods.reset({
        services: {
          cards: [],
          parking: [],
          pool: [],
          recreationalSports: [],
          specialMenus: [],
          spa: false,
          gym: false,
          wifi: false,
          breakfast: false,
          casino: false,
          petsAllowed: false,
          cafeteriaBar: false,
          restaurant: false,
          eventRooms: [],
          isAccessible: false,
        },
        rates: [],
      });
    }
  }, [accommodation, mode, methods]);

  const handleTabChange = (value: string) => setActiveTab(value);

  const handleNextTab = () => {
    const tabOrder = ["general", "services", "rates", "repat", "samples"];
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  const handleReset = () => {
    methods.reset();
    toast({
      title: "Formulario restablecido",
      description: "Todos los campos han sido restablecidos a sus valores iniciales.",
    });
  };

  const handleUnlockSampling = () => {
    if (!establishmentId) {
      toast({
        title: "Error",
        description: "No se pudo identificar el alojamiento.",
        variant: "destructive",
      });
      return;
    }

    unlockSampling(establishmentId);
  };

  const handleSave = methods.handleSubmit((data) => {
    if (mode === "edit") {
      console.log('UPDATING:', data);
      updateAccommodation(data, {
        onSuccess: () => {
          toast({
            title: "Alojamiento actualizado",
            description: "Los datos fueron actualizados correctamente.",
          });
        },
        onError: (error: AxiosError<ErrorResponse>) => {
          methods.reset(accommodation);
          toast({
            title: "Error",
            description: error?.response?.data?.message || "Ocurrió un error al actualizar.",
            variant: "destructive",
          });
        },
      });
    } else {
      createAccommodation(data, {
        onSuccess: (response) => {
          toast({
            title: "Alojamiento creado",
            description: "Los datos fueron enviados correctamente.",
          });
          onCreated?.(response.id.toString());

          /*
          createNotification({
            accommodationId: response.id.toString(),
            notificationStatusId: 1,
            notificationTypeId: 1,
            originUserRole: getUserRole() || '',
            targetUserRole: 'observatorio_observador',
            description: "Se ha creado un nuevo alojamiento",
          });
          */
        },
        onError: (error: AxiosError<ErrorResponse>) => {
          methods.reset();
          toast({
            title: "Error",
            description: error?.response?.data?.message || "Ocurrió un error al guardar.",
            variant: "destructive",
          });
        },
      });
    }
  });

  const isReadOnly = mode === "view";

  const handleSaveObservatoryNotes = () => {
    saveNotes({ observatoryNotes: observatoryNotesValue || null });
  };

  if (isLoadingAccommodation) {
    return <div>Cargando...</div>;
  }

  return (
    <FormProvider {...methods}>
      <RoleBasedElement requiredRoles={[ROLES.OBSERVATORIO_EDITOR]}>
      {isBlockedForSampling && (
        <SamplingBlockCard
          motivo={accommodation?.samplingBlockReason?.name || ""}
          fecha={accommodation?.samplingBlockDate
            ? new Date(accommodation.samplingBlockDate).toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : ""}
          isUnlocking={isUnlocking}
          isReadOnly={isReadOnly}
          onUnlock={handleUnlockSampling}
        />
      )}
      </RoleBasedElement>
      <form onSubmit={handleSave}>
        <Card className="w-full">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-4 mb-8">
                <TabsTrigger value="general">Datos Generales</TabsTrigger>
                <TabsTrigger value="services">Servicios</TabsTrigger>
                <TabsTrigger value="rates">Tarifas</TabsTrigger>
                <TabsTrigger value="repat">Repat</TabsTrigger>
              </TabsList>
              <TabsContent value="general">
                <AccomodationGeneralDataForm
                  readOnly={isReadOnly}
                  mode={mode}
                />
              </TabsContent>
              <TabsContent value="services">
                <ServicesForm readOnly={isReadOnly} />
              </TabsContent>
              <TabsContent value="rates">
                <RatesForm readOnly={isReadOnly} />
              </TabsContent>
              <TabsContent value="repat">
                <RepatForm readOnly={isReadOnly} />
              </TabsContent>
            </Tabs>
          </CardContent>
          <RoleBasedElement
            requiredRoles={[ROLES.ADMIN, ROLES.FISCALIZACION_EDITOR]}
          >
            <CardFooter className="flex justify-between border-t p-6 bg-gray-50">
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={isReadOnly}
                  className="gap-2"
                >
                  <RefreshCwIcon className="h-4 w-4" />
                  Restablecer
                </Button>
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleNextTab}
                  disabled={activeTab === "repat" || isReadOnly}
                  className="gap-2"
                >
                  Siguiente
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
                <Button type="submit" disabled={isReadOnly} className="gap-2">
                  <SaveIcon className="h-4 w-4" />
                  {mode === "edit" ? "Actualizar" : "Guardar"}
                </Button>
              </div>
            </CardFooter>
          </RoleBasedElement>
        </Card>
      </form>
      {mode !== "create" && (
        <Card className="w-full mt-4">
          <CardHeader>
            <CardTitle>Notas del Observatorio</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={observatoryNotesValue}
              onChange={(e) => setObservatoryNotesValue(e.target.value)}
              disabled={!isObservatoryEditor}
              className="min-h-[120px] resize-y"
              placeholder="Sin notas del observatorio"
            />
          </CardContent>
          <RoleBasedElement requiredRoles={[ROLES.OBSERVATORIO_EDITOR]}>
            <CardFooter className="border-t pt-4">
              <Button
                type="button"
                onClick={handleSaveObservatoryNotes}
                disabled={isSavingNotes}
                className="gap-2"
              >
                <SaveIcon className="h-4 w-4" />
                {isSavingNotes ? "Guardando..." : "Guardar notas"}
              </Button>
            </CardFooter>
          </RoleBasedElement>
        </Card>
      )}
    </FormProvider>
  );
};

export default AccomodationForm;
